import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Icon } from "@/components/ui/icon";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
export const SkillsManager = () => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    category: "",
    subcategory: "",
    skill_name: "",
    display_order: 0
  });
  const [newCategory, setNewCategory] = useState("");
  const [newSubcategory, setNewSubcategory] = useState("");
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [showNewSubcategory, setShowNewSubcategory] = useState(false);
  const {
    data: skills
  } = useQuery({
    queryKey: ["admin-skills"],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from("skills").select("*").order("category").order("display_order");
      if (error) throw error;
      return data;
    }
  });

  // Extract unique categories and subcategories
  const categories = useMemo(() => {
    if (!skills) return [];
    return Array.from(new Set(skills.map(s => s.category))).sort();
  }, [skills]);
  const subcategories = useMemo(() => {
    if (!skills || !formData.category) return [];
    return Array.from(new Set(skills.filter(s => s.category === formData.category && s.subcategory).map(s => s.subcategory!))).sort();
  }, [skills, formData.category]);
  const addMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const categoryToUse = showNewCategory ? newCategory : data.category;
      const subcategoryToUse = showNewSubcategory ? newSubcategory : data.subcategory;
      const {
        error
      } = await supabase.from("skills").insert([{
        ...data,
        category: categoryToUse,
        subcategory: subcategoryToUse || null
      }]);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-skills"]
      });
      queryClient.invalidateQueries({
        queryKey: ["skills"]
      });
      toast.success("Skill added successfully");
      setFormData({
        category: "",
        subcategory: "",
        skill_name: "",
        display_order: 0
      });
      setNewCategory("");
      setNewSubcategory("");
      setShowNewCategory(false);
      setShowNewSubcategory(false);
    }
  });
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const {
        error
      } = await supabase.from("skills").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin-skills"]
      });
      queryClient.invalidateQueries({
        queryKey: ["skills"]
      });
      toast.success("Skill deleted successfully");
    }
  });
  const groupedSkills = skills?.reduce((acc, skill) => {
    if (!acc[skill.category]) {
      acc[skill.category] = [];
    }
    acc[skill.category].push(skill);
    return acc;
  }, {} as Record<string, typeof skills>);
  return <div className="space-y-6">
      <Card className="p-6 bg-card border-border shadow-sm rounded-xl">
        <h2 className="text-2xl font-bold text-foreground mb-6">Add New Skill</h2>
        <form onSubmit={e => {
        e.preventDefault();
        addMutation.mutate(formData);
      }} className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              {showNewCategory ? <div className="flex gap-2">
                  <Input value={newCategory} onChange={e => setNewCategory(e.target.value)} placeholder="Enter new category" required />
                  <Button type="button" variant="outline" onClick={() => {
                setShowNewCategory(false);
                setNewCategory("");
              }}>
                    <Icon name="close" size={16} />
                  </Button>
                </div> : <div className="flex gap-2">
                  <Select value={formData.category} onValueChange={value => {
                if (value === "__new__") {
                  setShowNewCategory(true);
                  setFormData({
                    ...formData,
                    category: "",
                    subcategory: ""
                  });
                } else {
                  setFormData({
                    ...formData,
                    category: value,
                    subcategory: ""
                  });
                }
              }}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => <SelectItem key={cat} value={cat}>
                          {cat}
                        </SelectItem>)}
                      <SelectItem value="__new__">
                        <span className="flex items-center gap-2">
                          <Icon name="add" size={16} />
                          Add New Category
                        </span>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="subcategory">Subcategory</Label>
              {showNewSubcategory ? <div className="flex gap-2">
                  <Input value={newSubcategory} onChange={e => setNewSubcategory(e.target.value)} placeholder="Enter new subcategory" />
                  <Button type="button" variant="outline" onClick={() => {
                setShowNewSubcategory(false);
                setNewSubcategory("");
              }}>
                    <Icon name="close" size={16} />
                  </Button>
                </div> : <Select value={formData.subcategory} onValueChange={value => {
              if (value === "__new__") {
                setShowNewSubcategory(true);
                setFormData({
                  ...formData,
                  subcategory: ""
                });
              } else if (value === "__none__") {
                setFormData({
                  ...formData,
                  subcategory: ""
                });
              } else {
                setFormData({
                  ...formData,
                  subcategory: value
                });
              }
            }} disabled={!formData.category && !showNewCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select subcategory (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {subcategories.map(subcat => <SelectItem key={subcat} value={subcat}>
                        {subcat}
                      </SelectItem>)}
                    <SelectItem value="__new__">
                      <span className="flex items-center gap-2">
                        <Icon name="add" size={16} />
                        Add New Subcategory
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="skill_name">Skill Name *</Label>
            <Input id="skill_name" value={formData.skill_name} onChange={e => setFormData({
            ...formData,
            skill_name: e.target.value
          })} required />
          </div>

          <Button type="submit" disabled={addMutation.isPending} className="w-full">
            {addMutation.isPending ? "Adding..." : "Add Skill"}
          </Button>
        </form>
      </Card>

      <div className="space-y-4">
        <h3 className="text-xl font-semibold text-foreground">Existing Skills</h3>
        {groupedSkills && Object.entries(groupedSkills).map(([category, categorySkills]) => <Card key={category} className="p-6 bg-card border-border shadow-sm rounded-xl">
              <h4 className="text-lg font-semibold text-foreground mb-4">{category}</h4>
              <div className="flex flex-wrap gap-2">
                {categorySkills.map(skill => <Badge key={skill.id} variant="secondary" className="flex items-center gap-2 px-3 py-1 bg-primary">
                    {skill.skill_name}
                    <button onClick={() => deleteMutation.mutate(skill.id)} className="hover:text-destructive">
                      <Icon name="close" size={14} />
                    </button>
                  </Badge>)}
              </div>
            </Card>)}
      </div>
    </div>;
};